/** biome-ignore-all lint/style/useNamingConvention: expected */
import type Path from "@core/FileProvider/Path/Path";
import {
	type ArticleError,
	type FrontmatterFailure,
	frontmatterFailure,
} from "@core/FileStructue/Article/ArticleError";
import FrontmatterError from "@core/FileStructue/Article/FrontmatterError";
import type { Catalog } from "@core/FileStructue/Catalog/Catalog";
import type FileStructure from "@core/FileStructue/FileStructure";
import type { MarkdownProps } from "@core/FileStructue/FileStructure";
import setItemProp from "@core/FileStructue/Item/ItemPropsUtils";
import type { ItemRef } from "@core/FileStructue/Item/ItemRef";
import { ItemType } from "@core/FileStructue/Item/ItemType";
import type Hasher from "@core/Hash/Hasher";
import type ResourceUpdater from "@core/Resource/ResourceUpdater";
import createNewFilePathUtils from "@core/utils/createNewFilePathUtils";
import { RwLock } from "@core/utils/rwlock";
import type { InboxProps } from "@ext/inbox/models/types";
import { addEvent, Level } from "@ext/loggers/opentelemetry";
import type PrivateParserContext from "@ext/markdown/core/Parser/ParserContext/PrivateParserContext";
import { FileStatus } from "@ext/Watchers/model/FileStatus";
import type { JSONContent } from "@tiptap/core";
import type { RenderableTreeNode } from "../../../extensions/markdown/core/render/logic/Markdoc";
import type { TocItem } from "../../../extensions/navigation/article/logic/createTocItems";
import type { Category } from "../Category/Category";
import { Item, type ItemEvents, type ItemProps, NAV_STRUCTURAL_PROPS, type UpdateItemProps } from "../Item/Item";

export type ArticleEvents = ItemEvents;

export type ArticleInitProps<P extends ItemProps> = {
	ref: ItemRef;
	parent: Category;
	content: string | null;
	props: P;
	logicPath: string;

	fs: FileStructure;

	lastModified: number;
	error?: ArticleError;
	/** For an article handed its content already read, where `_loadContent` returns too early to say. */
	bodyStartLine?: number;
};

export type ArticleProps =
	| ({
			welcome?: boolean;
	  } & ItemProps)
	| InboxProps;

export const ArticlePropsKeys = [
	"title",
	"properties",
	"date",
	"author",
	"description",
	"template",
	"customProperties",
	"quiz",
	"expanded",
	"searchPhrases",
] as const;

export class Article<P extends ArticleProps = ArticleProps> extends Item<P> {
	protected _fs: FileStructure;

	protected _parsedContent = RwLock.store<Content>(null);
	protected _content: string | null;
	protected _lastModified: number;
	private _error?: ArticleError;
	private _staleMarks = 0;
	private _parsedAtMark = 0;

	constructor({ ref, parent, props, logicPath, ...init }: ArticleInitProps<P>) {
		super(ref, parent, props, logicPath);
		this._content = init.content;
		this._lastModified = init.lastModified;
		this._fs = init.fs;
		this._bodyStartLine = init.bodyStartLine;
		this._setError(init.error);
	}

	get events() {
		return super.events;
	}

	get parsedContent() {
		return this._parsedContent;
	}

	/** The cached parse stays readable, but no longer counts as current: content it embeds has changed. */
	markParseStale(): void {
		this._staleMarks++;
	}

	isParseStale(): boolean {
		return this._parsedAtMark !== this._staleMarks;
	}

	/**
	 * Call before a parse reads anything, and call what it returns once that parse is cached, under the write lock:
	 * the parse then counts as current, unless the article was marked stale in between.
	 */
	beginParse(): () => void {
		const mark = this._staleMarks;
		return () => {
			this._parsedAtMark = mark;
		};
	}

	get lastModified() {
		return this._lastModified;
	}

	async getContent(): Promise<string> {
		await this._loadContent();
		const mutableContent = { content: this._content ?? "" };
		await this.events.emit("item-get-content", { item: this, mutableContent });
		return mutableContent.content;
	}

	hasContent(): boolean {
		return !this._props.shouldBeCreated;
	}

	async reloadFromDisk(catalog?: Catalog): Promise<void> {
		this._content = null;
		this._lastModified = Date.now();
		await this._parsedContent.write(() => null);

		if (this.props.shouldBeCreated) return;
		try {
			const raw = await this._fs.fp.read(this._ref.path);
			const parsed = this._fs.parseMarkdown(raw.toString());
			this._syncFrontmatter(parsed);
			const mutable = { content: "", props: parsed.props };
			if (catalog) await this._fs.events.emit("item-read", { catalog, mutable });
			this._updateProps(mutable.props, NAV_STRUCTURAL_PROPS);
		} catch (e) {
			addEvent("article-refresh-failed", Level.Commands, { path: this._ref.path.value, error: String(e) });
		}
	}

	private _bodyStartLine: number;

	get type() {
		return ItemType.article;
	}

	get error(): ArticleError | undefined {
		return this._error;
	}

	/** Turns a line counted in the body into a line of the file. `1` until the content has been read. */
	get bodyStartLine(): number {
		return this._bodyStartLine ?? 1;
	}

	private get _brokenFrontmatter(): FrontmatterFailure | undefined {
		return this._error?.type === "frontmatter" ? this._error : undefined;
	}

	/** Records what the last read said — including that it said nothing, so a repair outside Gramax lands. */
	private _syncFrontmatter(parsed: MarkdownProps) {
		const failure = frontmatterFailure(parsed.error?.message, parsed.errorLine);
		if (failure) return this._setError(failure);
		if (!this._brokenFrontmatter) return;
		this._error = undefined;
		addEvent("frontmatter-readable", Level.Commands, { path: this._ref.path.value });
	}

	private _setError(error: ArticleError | undefined) {
		if (!error) return;
		this._error = error;
		if (error.type === "frontmatter")
			addEvent("frontmatter-unreadable", Level.Commands, {
				path: this._ref.path.value,
				error: error.message,
				line: error.line,
			});
	}

	/** A write this article refused is news to whoever edited it; a save on a neighbour's behalf stays silent. */
	private _throwIfWriteRefused() {
		const broken = this._brokenFrontmatter;
		if (broken) throw new FrontmatterError(this._ref.path, broken.message, { line: broken.line });
	}

	async updateContent(content: string, dropParsedContent = true) {
		this._content = content;
		if (dropParsedContent) await this._parsedContent.write(() => null);
		await this._save();
	}

	/** The file as it lies on disk, or null when there is none yet. */
	async readFile(): Promise<string | null> {
		if (this.props.shouldBeCreated || !(await this._fs.fp.exists(this._ref.path))) return null;
		return (await this._fs.fp.read(this._ref.path))?.toString() ?? null;
	}

	/** An edit of the body by the article's own editor; unlike `updateContent`, a refused write is reported. */
	async editContent(content: string) {
		await this.updateContent(content);
		this._throwIfWriteRefused();
	}

	/**
	 * Writes the article from a whole file, frontmatter included — the one way to repair an article
	 * whose frontmatter never parsed, so the frontmatter is judged again on every such edit.
	 */
	async rawUpdateContent(rawContent: string, dropParsedContent = true) {
		const parsed = this._fs.parseMarkdown(rawContent);
		const { content, props, error } = parsed;
		const wasUnreadable = Boolean(this._brokenFrontmatter);
		this._syncFrontmatter(parsed);

		// The edit is the only account of the props there is, so it replaces them whole: merging
		// would keep only the keys `_updateProps` knows and drop the rest, `order` among them.
		if (wasUnreadable && !error) this._props = props as P;
		else this._updateProps(props);

		await this.updateContent(content, dropParsedContent);
		this._throwIfWriteRefused();
	}

	getFileName(): string {
		return this._ref.path.name;
	}

	protected _updateProps(props: UpdateItemProps, additionalKeys: readonly string[] = []) {
		const allProps = this._props as Record<string, unknown>;

		for (const key of [...ArticlePropsKeys, ...additionalKeys]) {
			if (!(key in props)) {
				delete allProps[key];
				continue;
			}

			if (Array.isArray(props[key])) {
				if (props[key]?.length) setItemProp(allProps, key, props[key]);
				else delete allProps[key];
			} else setItemProp(allProps, key, props[key]);
		}
	}

	save() {
		return this._save();
	}

	async hash(hash: Hasher, recursive = true) {
		const hasher = await super.hash(hash);
		hasher.hash(this._content ?? "");
		if (recursive) await this.parsedContent.read((p) => p.parsedContext.getResourceManager().hash(hash));
		return hasher;
	}

	/** A save that no longer describes the file is dropped: the file stays as the last reader found it. */
	private _skipSave(error: string) {
		addEvent("save-skipped", Level.Commands, { path: this._ref.path.value, error });
	}

	protected async _save(renamed?: boolean) {
		// `_props` no longer describes the file, so writing would replace real frontmatter with nothing.
		if (this._brokenFrontmatter) return this._skipSave(this._brokenFrontmatter.message);

		delete this._props.shouldBeCreated;
		delete this._props.welcome;
		if (this._content === null) {
			try {
				await this._loadContent();
			} catch (error) {
				// Nothing in memory describes the file, and this save is usually about a neighbour —
				// the empty body it used to fall back to would overwrite an untouched article.
				addEvent("save-skipped-unreadable-file", Level.Commands, {
					path: this._ref.path.value,
					error: String(error),
				});
				return;
			}

			// The lazy read is the first look at the file since the scan, so a break that arrived
			// after it — a pull, an editor outside Gramax — is known only here, and `_props` are
			// still the ones the scan read.
			if (this._brokenFrontmatter) return this._skipSave(this._brokenFrontmatter.message);
		}

		await this.events.emit("item-pre-save", {
			item: this,
			mutable: { content: this._content ?? "", props: this._props },
		});
		if (this._props.title?.toString()?.trim()) delete this._props.external;

		// A save can move where the body begins, and `_loadContent` returns too early to notice.
		this._bodyStartLine = await this._fs.saveArticle(this._ref.path, this._content ?? "", this._props);
		this._lastModified = Date.now();
		await this.events.emit("item-changed", { item: this, status: renamed ? FileStatus.new : FileStatus.modified });
	}

	protected override async _updateFilename(fileName: string, resourceUpdater: ResourceUpdater, catalog: Catalog) {
		if (this.getFileName() === fileName) return;
		let path = this._ref.path.getNewName(fileName);
		if (await this._fs.fp.exists(path)) {
			const readdir = await this._fs.fp.getItems(this.ref.path.parentDirectoryPath);
			path = createNewFilePathUtils.create(
				this.ref.path,
				readdir.map((s) => s.path),
				fileName,
			);
		}
		await this._fs.moveArticle(this, path);
		this._lastModified = Date.now();
		const newArticle = await this._getUpdateArticleByProps(path, catalog);
		this._logicPath = newArticle.logicPath;
		this._ref = newArticle.ref;
		this._content = newArticle._content;
		await resourceUpdater.update(this, newArticle);
		await this.parsedContent.write(() => newArticle.parsedContent.read());

		return this;
	}

	protected async _getUpdateArticleByProps(path: Path, catalog: Catalog) {
		return await this._fs.makeArticleByProps(
			path,
			this._props,
			this._content,
			this._parent,
			this._lastModified,
			catalog,
			this._bodyStartLine,
		);
	}

	private async _loadContent(): Promise<void> {
		if (this._content !== null) return;
		if (this.props.shouldBeCreated) return;

		const raw = await this._fs.fp.read(this._ref.path);
		const parsed = this._fs.parseMarkdown(raw.toString());
		this._content = parsed.content;
		this._bodyStartLine = parsed.bodyStartLine ?? 1;
		this._syncFrontmatter(parsed);
	}
}

export interface Content {
	tocItems: TocItem[];
	editTree: JSONContent;
	renderTree: RenderableTreeNode;
	parsedContext: PrivateParserContext;
}
